import { useEffect } from 'react';

// Norme Happn, toute l'app : un bouton qui porte un texte n'a jamais d'icône, et tient sur UNE ligne.
// Le hook marque les boutons (`hp-noicon` = svg masqué par CSS, `hp-textbtn` = 1 ligne) ; il ne touche pas aux boutons icône seule.
// Un libellé qui ne tient pas est raccourci par « … » (`hp-ellip`, texte complet dans `title`) : mesuré après rendu, recalculé au redimensionnement.
// Les tableaux à en-têtes reçoivent `hp-stack` + `data-label` par cellule → empilés en fiches sur téléphone (CSS overflow-fixes.css).
const SEL = 'button, a.btn, a[class*="btn"], a[class*="cta"], [role="button"]:not(li):not(article):not(div), [role="tab"]';
const KEEP = '.ab-btn, .hp-icon, .fh-num, .kit-badge, .home-section-icon, .widget-card-icon, .sab-badge, .stb-badge, .hp-tile-icon, .agent-mark, .avatar, .fh-say-avatar, [data-keepicon], .hp-choice, .kit-chip, .fh-chip, .fh-tag, .hp-x, .hp-iconbtn, .fh-iconbtn, .fh-go';
// Boutons « carte » (plusieurs lignes de texte structurées) : jamais réduits à une ligne
const CARDLIKE = '[class*="-row"], [class*="card"], [class*="sheet"], .ml-sugg-prio-open, .ml-alerte, .rc-fiab-row, .fhero-fiab-row, .fv-tile-line, .fv-dd-item, .fb-tl, .fb-g3-r, .fb-rv, .stkm-prod, .stkm-eq, .stkm-zone, .stkm-temps-b, .mp-st > button, .fig-chiffre';
const BLOCKY = 'div, p, h1, h2, h3, h4, h5, h6, ul, ol, li, img, picture, figure, table, section, article, header, footer, textarea, input, select';
const NOSTACK = '.ptv-table, [data-nostack], [data-testid="employee-fiche-semaine-table"]';

const mark = (root) => {
  root.querySelectorAll(SEL).forEach((el) => {
    if (el.matches(KEEP) || el.closest('[data-keepicon], .hp-choice, .kit-chip, .fh-chip, .ab-card, .acm-tl, .fb-tl')) return;
    const text = (el.textContent || '').trim();
    if (text.length < 2) return; // icône seule
    const svgs = [...el.querySelectorAll('svg')].filter((s) => !s.closest(KEEP) && !s.classList.contains('animate-spin'));
    if (svgs.length) el.classList.add('hp-noicon');
    if (!el.querySelector(BLOCKY) && text.length <= 64 && !el.matches(CARDLIKE)) el.classList.add('hp-textbtn');
  });
};

// Lecture groupée puis écriture : pas de va-et-vient de mise en page
const fit = (root, reset) => {
  const btns = [...root.querySelectorAll('.hp-textbtn')];
  if (reset) btns.forEach((el) => el.classList.remove('hp-ellip'));
  const over = btns.filter((el) => !el.classList.contains('hp-ellip') && el.clientWidth > 0 && el.scrollWidth > el.clientWidth + 1);
  over.forEach((el) => { el.classList.add('hp-ellip'); if (!el.title) el.title = (el.textContent || '').trim().replace(/\s+/g, ' '); });
};

const stack = (root) => {
  root.querySelectorAll('table:not(.hp-stack)').forEach((t) => {
    if (t.matches(NOSTACK) || t.closest('[data-nostack]')) return;
    const ths = [...t.querySelectorAll(':scope > thead th')];
    if (ths.length < 3) return;
    t.classList.add('hp-stack');
  });
  root.querySelectorAll('table.hp-stack').forEach((t) => {
    const labels = [...t.querySelectorAll(':scope > thead th')].map((th) => (th.textContent || '').trim().replace(/\s+/g, ' '));
    t.querySelectorAll(':scope > tbody > tr').forEach((tr) => {
      let i = 0;
      [...tr.children].forEach((td) => { const l = labels[i] || ''; if (l && td.getAttribute('data-label') !== l) td.setAttribute('data-label', l); i += td.colSpan || 1; });
    });
  });
};

// Rails défilants (filtres, onglets, tuiles) : `hp-more` tant qu'il reste du contenu à droite → fondu CSS (overflow-fixes.css)
const RAILS = '.kit-pills, .fh-rail, .hp-rail, .hp-chips, .fh-chips, .ab-row, .ab-views-row, .tp-filters, .act-filters, .equipment-tabs, .tp-week, .tp-prime-line, .m-rail, .cs-zones, .hp-chiprow, .kit-chips, .da-chips, .fin-period-kinds, .stk-view-toggle, .xp-row, .xp-sugg-btns, .flex.overflow-x-auto, [data-testid="planning-day-strip"], [data-testid^="poste-week-"], .flex.flex-wrap:has(> button)';
const more = (el) => {
  const on = el.scrollWidth > el.clientWidth + 2 && el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
  el.classList.toggle('hp-more', on);
  if (!el.dataset.hpRail) { el.dataset.hpRail = '1'; el.addEventListener('scroll', () => more(el), { passive: true }); }
};
const rails = (root) => root.querySelectorAll(RAILS).forEach((el) => { if (el.scrollWidth > el.clientWidth + 2 || el.classList.contains('hp-more')) more(el); });

// LOT 79 · seules les parties ajoutées sont relues (plus la page entière à chaque image) ; la mesure des libellés trop longs attend un temps calme
const contenu = (racines) => { const l = [...racines].filter((r) => r.isConnected); return l.length > 40 ? [document.body] : l.filter((r) => !l.some((a) => a !== r && a.contains(r))); };
const autour = (r, sel) => { const c = r.closest?.(sel); return c && c !== r ? c : null; };
const cadre = (r, sel) => (autour(r, sel) || r).parentElement || r;

export const useHappnButtons = () => {
  useEffect(() => {
    let raf = 0; let file = new Set([document.body]); let lent = 0; const aMesurer = new Set();
    const mesurer = (reset) => { const l = contenu(aMesurer); aMesurer.clear(); l.forEach((r) => { fit(cadre(r, SEL), reset); rails(cadre(r, RAILS)); }); };
    const run = (reset) => { raf = 0; const l = contenu(file); file = new Set(); l.forEach((r) => { mark(cadre(r, SEL)); stack(autour(r, 'table') || r); aMesurer.add(r); }); clearTimeout(lent); lent = setTimeout(() => mesurer(reset === true), 180); };
    run();
    const obs = new MutationObserver((recs) => { recs.forEach((m) => m.addedNodes.forEach((n) => { const e = n.nodeType === 1 ? n : n.parentElement; if (e) file.add(e); })); if (file.size && !raf) raf = requestAnimationFrame(run); });
    obs.observe(document.body, { childList: true, subtree: true });
    let rz = 0;
    const onResize = () => { clearTimeout(rz); rz = setTimeout(() => { file.add(document.body); run(true); }, 120); };
    window.addEventListener('resize', onResize);
    return () => { obs.disconnect(); window.removeEventListener('resize', onResize); clearTimeout(rz); clearTimeout(lent); if (raf) cancelAnimationFrame(raf); };
  }, []);
};
